/**
 * Data alignment utilities for merging sparse time series data onto a common timeline
 * Replicates the functionality of pondjs TimeSeries.fixedWindowRollup()
 */

// Type definitions for DataSeries (matching parser types)
export interface CompressedTimeSeries {
  startTime: number;
  interval: number;
  count: number;
  values?: number[]; // Optional: only if values are not all zeros
}

export interface DataSeries {
  columns: string[];
  points?: Array<[number, number]>; // Optional: used for small datasets or non-uniform data
  compressed?: CompressedTimeSeries; // Optional: used for large uniform time series
}

export interface TimelinePoint {
  timestamp: number;
  value: number;
}

export interface DataPoint {
  timestamp: number;
  value: any;
  metadata?: any;
}

export type AggregationMethod = "avg" | "max" | "min" | "sum" | "count" | "first" | "last";

/**
 * Aggregates values using the specified method
 */
export const aggregateValues = (values: any[], method: AggregationMethod): number | null => {
  if (!values || values.length === 0) {
    return null;
  }

  const numericValues = values.filter((v) => typeof v === "number" && !isNaN(v));
  if (numericValues.length === 0) {
    return null;
  }

  switch (method) {
    case "avg":
      return numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length;
    case "sum":
      return numericValues.reduce((sum, val) => sum + val, 0);
    case "min":
      return Math.min(...numericValues);
    case "max":
      return Math.max(...numericValues);
    case "count":
      return numericValues.length;
    case "first":
      return numericValues[0];
    case "last":
      return numericValues[numericValues.length - 1];
    default:
      return numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length;
  }
};

/**
 * Parses time string like "3s", "5m", "1h" to milliseconds
 */
export const parseTimeString = (timeStr: string): number => {
  const match = timeStr.match(/^(\d+)([smh])$/);
  if (!match) {
    throw new Error(`Invalid time string: ${timeStr}`);
  }

  const value = parseInt(match[1], 10);
  const unit = match[2];

  switch (unit) {
    case "s":
      return value * 1000;
    case "m":
      return value * 60 * 1000;
    case "h":
      return value * 60 * 60 * 1000;
    default:
      throw new Error(`Invalid time unit: ${unit}`);
  }
};

/**
 * Aligns sparse data points to a timeline using fixed window rollup
 * This replicates pondjs TimeSeries.fixedWindowRollup() behavior
 */
export const alignDataToTimeline = (
  timeline: TimelinePoint[],
  dataPoints: Array<[number, any]>, // [timestamp, value] format
  windowSize: string | number = "3s", // e.g., "3s", "5s" or milliseconds
  aggregationMethod: AggregationMethod = "avg"
): DataPoint[] => {
  if (!timeline || timeline.length === 0) {
    return [];
  }
  if (!dataPoints || dataPoints.length === 0) {
    // Return timeline with null values if no data
    return timeline.map((point) => ({
      timestamp: point.timestamp,
      value: null,
      metadata: {aggregated: true, method: aggregationMethod, windowSize}
    }));
  }

  const windowSizeMs = typeof windowSize === "string" ? parseTimeString(windowSize) : windowSize;

  // Get timeline bounds
  const startTime = timeline[0].timestamp;
  const endTime = timeline[timeline.length - 1].timestamp;

  // Align window start to timeline
  const alignedStart = Math.floor(startTime / windowSizeMs) * windowSizeMs;

  const result: DataPoint[] = [];

  // Process each window
  for (let windowStart = alignedStart; windowStart <= endTime; windowStart += windowSizeMs) {
    const windowEnd = windowStart + windowSizeMs;

    // Find data points in this window
    const pointsInWindow = dataPoints.filter((point) => point[0] >= windowStart && point[0] < windowEnd);

    let aggregatedValue: any = null;

    if (pointsInWindow.length > 0) {
      const values = pointsInWindow.map((point) => point[1]);
      aggregatedValue = aggregateValues(values, aggregationMethod);
    }

    // Find the closest timeline point to window center
    const windowCenter = windowStart + windowSizeMs / 2;
    const closestTimelinePoint = timeline.reduce((closest, point) => {
      const closestDistance = Math.abs(closest.timestamp - windowCenter);
      const currentDistance = Math.abs(point.timestamp - windowCenter);
      return currentDistance < closestDistance ? point : closest;
    });

    result.push({
      timestamp: closestTimelinePoint.timestamp,
      value: aggregatedValue,
      metadata: {
        aggregated: true,
        method: aggregationMethod,
        windowSize: windowSizeMs,
        windowStart,
        windowEnd,
        originalCount: pointsInWindow.length
      }
    });
  }

  return result.sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Merges data points onto timeline without windowing (direct mapping)
 */
export const mergeDataToTimeline = (
  timeline: TimelinePoint[],
  dataPoints: Array<[number, any]>,
  fillMethod: "forward" | "backward" | "nearest" | "none" = "none"
): DataPoint[] => {
  if (!timeline || timeline.length === 0) {
    return [];
  }

  const result: DataPoint[] = [];

  for (const timelinePoint of timeline) {
    // Find exact match first
    const exactMatch = dataPoints.find((point) => point[0] === timelinePoint.timestamp);

    if (exactMatch) {
      result.push({
        timestamp: timelinePoint.timestamp,
        value: exactMatch[1],
        metadata: {exact: true}
      });
      continue;
    }

    // Handle fill methods
    let value: any = null;

    if (fillMethod === "nearest") {
      const nearest = dataPoints.reduce((closest, point) => {
        const closestDistance = Math.abs(closest[0] - timelinePoint.timestamp);
        const currentDistance = Math.abs(point[0] - timelinePoint.timestamp);
        return currentDistance < closestDistance ? point : closest;
      });
      value = nearest ? nearest[1] : null;
    } else if (fillMethod === "forward") {
      const previous = dataPoints.filter((point) => point[0] <= timelinePoint.timestamp).sort((a, b) => b[0] - a[0])[0];
      value = previous ? previous[1] : null;
    } else if (fillMethod === "backward") {
      const next = dataPoints.filter((point) => point[0] >= timelinePoint.timestamp).sort((a, b) => a[0] - b[0])[0];
      value = next ? next[1] : null;
    }

    result.push({
      timestamp: timelinePoint.timestamp,
      value,
      metadata: {filled: fillMethod !== "none", method: fillMethod}
    });
  }

  return result;
};

/**
 * Validates timeline data
 */
export const validateTimeline = (timeline: TimelinePoint[]): {isValid: boolean; errors: string[]} => {
  const errors: string[] = [];

  if (!Array.isArray(timeline)) {
    errors.push("Timeline must be an array");
    return {isValid: false, errors};
  }

  if (timeline.length === 0) {
    errors.push("Timeline cannot be empty");
    return {isValid: false, errors};
  }

  for (let i = 0; i < timeline.length; i++) {
    const point = timeline[i];

    if (!point || typeof point.timestamp !== "number") {
      errors.push(`Invalid timestamp at index ${i}`);
    }

    if (i > 0 && point.timestamp <= timeline[i - 1].timestamp) {
      errors.push(`Timeline not in chronological order at index ${i}`);
    }
  }

  return {isValid: errors.length === 0, errors};
};

/**
 * Helper function to extract points from DataSeries (handles both points and compressed formats)
 * Used to convert DataSeries to [timestamp, value][] format for charting
 */
export const dataSeriesToPoints = (dataSeries?: DataSeries): Array<[number, number]> => {
  if (!dataSeries) {
    return [];
  }

  // If already in points format, return it
  if (dataSeries.points) {
    return dataSeries.points;
  }

  // Convert compressed format to points array
  if (dataSeries.compressed) {
    const {startTime, interval, count, values} = dataSeries.compressed;
    const points: Array<[number, number]> = [];
    for (let i = 0; i < count; i++) {
      const timestamp = startTime + i * interval;
      const value = values?.[i] ?? 0; // Use value from array or default to 0
      points.push([timestamp, value]);
    }
    return points;
  }

  return [];
};

/**
 * Helper function to get the point count from DataSeries (handles both points and compressed formats)
 */
export const dataSeriesLength = (dataSeries?: DataSeries): number => {
  if (!dataSeries) {
    return 0;
  }

  if (dataSeries.points) {
    return dataSeries.points.length;
  }

  if (dataSeries.compressed) {
    return dataSeries.compressed.count;
  }

  return 0;
};

/**
 * Utility to convert xaxis.points format to timeline format
 */
export const xaxisToTimeline = (xaxisPoints: Array<[number, number]>): TimelinePoint[] => {
  return xaxisPoints.map((point) => ({
    timestamp: point[0],
    value: point[1]
  }));
};
